import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { PhoneNumberCopy } from "@/components/ui/phone-number-copy";
import type { OutboundCallBackend } from "@/services/outboundCallService";
import { getAssistantLanguageSetting } from "@/services/assistantLanguageService";
import { StartLanguageSelect } from "@/components/shared/StartLanguageSelect";
import type { LanguageConfiguration } from "@/config/language-rules";

interface ConfirmCallDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  leadName: string;
  phoneNumber: string;
  agentName: string;
  featureTemplateName?: string; // New prop
  onConfirm: () => Promise<void>;
  variables?: Record<string, any>;
  backend?: OutboundCallBackend;
  onBackendChange?: (backend: OutboundCallBackend) => void;
  /** The assistant whose call is confirmed; its saved language setting feeds the start language. */
  assistantId?: string | null;
  /** A language code, or '' for the assistant default. */
  startLanguage?: string;
  onStartLanguageChange?: (language: string) => void;
}

export function ConfirmCallDialog({
  open,
  onOpenChange,
  leadName,
  phoneNumber,
  agentName,
  featureTemplateName, // New prop
  onConfirm,
  variables = {},
  backend = "v1",
  onBackendChange,
  assistantId,
  startLanguage = "",
  onStartLanguageChange,
}: ConfirmCallDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [languageSetting, setLanguageSetting] =
    useState<LanguageConfiguration | null>(null);

  // Load the saved language setting while the dialog is open on V2.
  useEffect(() => {
    if (!open || backend !== "v2" || !assistantId || !onStartLanguageChange) {
      setLanguageSetting(null);
      return;
    }
    let cancelled = false;
    getAssistantLanguageSetting(assistantId).then((setting) => {
      if (!cancelled) setLanguageSetting(setting);
    });
    return () => {
      cancelled = true;
    };
  }, [open, backend, assistantId, onStartLanguageChange]);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (error) {
      console.error("Error initiating call:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Confirm Call</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Lead</p>
              <p className="font-medium">{leadName}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Phone</p>
              <div className="font-medium">
                <PhoneNumberCopy phoneNumber={phoneNumber} />
              </div>
            </div>
          </div>

          <div>
            <p className="text-sm font-medium text-muted-foreground">Agent</p>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-primary/10">
                {agentName}
              </Badge>
              {featureTemplateName && (
                <span className="text-sm text-muted-foreground">
                  ({featureTemplateName})
                </span>
              )}
            </div>
          </div>

          {onBackendChange && (
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-2">
                Backend
              </p>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={backend === "v1" ? "default" : "outline"}
                  onClick={() => onBackendChange("v1")}
                  disabled={isSubmitting}
                >
                  V1
                </Button>
                <Button
                  type="button"
                  variant={backend === "v2" ? "default" : "outline"}
                  onClick={() => onBackendChange("v2")}
                  disabled={isSubmitting}
                >
                  V2
                </Button>
              </div>
            </div>
          )}

          {backend === "v2" && onStartLanguageChange && (
            <StartLanguageSelect
              setting={languageSetting}
              value={startLanguage}
              onChange={onStartLanguageChange}
              disabled={isSubmitting}
            />
          )}

          {Object.keys(variables).length > 0 && (
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-2">
                Campaign Configuration
              </p>
              <div className="bg-muted p-3 rounded-md">
                <pre className="text-xs whitespace-pre-wrap">
                  {JSON.stringify(variables, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between sm:justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Initiating...
              </>
            ) : (
              "Start Call"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
